// Global Instructions Rule Applied!

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useDrawer } from '../../../core/contexts/DrawerContext.jsx'
import { ContactRecordFields } from '../../../state_manager/data_models/contact_record.ts'
import { ContactRecord } from '../../../state_manager/data_models/contact_record.ts'
import { selectActiveRecordById } from '../../../state_manager/state_update_actions/active_records/selectors.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_CONTACTS } from '../../../state_manager/globals.ts'

/**
 * Custom hook for managing drawer state and operations for contacts
 * Handles drawer opening, closing, and form submission logic
 */
export const useContactsDrawer = (contacts: ContactRecord[]) => {
  const { isOpen: isDrawerOpen, recordId, isNew, openDrawerForNew, openDrawerForRecord, closeDrawer } = useDrawer()
  const navigate = useNavigate()
  const { openFormInTab } = useTabManagerContext()

  const [selectedContact, setSelectedContact] = useState<ContactRecord | null>(null)

  const handleOpenDrawerForNew = useCallback(() => {
    setSelectedContact(null)
    openDrawerForNew()
  }, [openDrawerForNew])

  const handleOpenDrawerForContact = useCallback(
    (contactId: string | number) => {
      const contact = contacts.find((c) => c[ContactRecordFields.contacts__id]?.toString() === contactId.toString())
      setSelectedContact(contact || null)
      openDrawerForRecord(contactId)
    },
    [contacts, openDrawerForRecord]
  )

  const handleContactNameClick = useCallback(
    (key: string, value: any, contactId: string | number) => {
      if (contactId) {
        handleOpenDrawerForContact(contactId)
      }
    },
    [handleOpenDrawerForContact]
  )

  const handleContactSecondaryNameClick = useCallback(
    (key: string, value: any, contactId: string | number) => {
      if (contactId) {
        openFormInTab(TABLE_CONTACTS, contactId)
      }
    },
    [openFormInTab]
  )

  const handleTasksClick = useCallback(
    (tasks: any[], contactRecord: ContactRecord, taskId: string | null = null) => {
      const contactId = contactRecord?.[ContactRecordFields.contacts__id]

      if (contactId) {
        const params = new URLSearchParams()

        if (taskId) {
          params.set('taskId', taskId)
        }

        const queryString = params.toString()
        navigate(`/contacts/forms/drawer/${contactId}${queryString ? `?${queryString}` : ''}`)
      }
    },
    [navigate]
  )

  const handleDrawerCancel = useCallback(() => {
    setSelectedContact(null)
    closeDrawer()
  }, [closeDrawer])

  // Get contact from activeRecords first
  const contactFromActiveRecords = useSelector((state: any) =>
    recordId && !isNew ? selectActiveRecordById(state, recordId) : null
  )

  useEffect(() => {
    if (recordId && !isNew) {
      // First check activeRecords, then fallback to contacts array
      const contact =
        contactFromActiveRecords ||
        contacts.find((c) => c[ContactRecordFields.contacts__id]?.toString() === recordId.toString())
      setSelectedContact(contact || null)
    } else if (isNew || !recordId) {
      setSelectedContact(null)
    }
  }, [recordId, isNew, contacts, contactFromActiveRecords])

  return {
    isDrawerOpen,
    recordId,
    isNew,
    selectedContact,
    handleOpenDrawerForNew,
    handleOpenDrawerForContact,
    handleContactNameClick,
    handleContactSecondaryNameClick,
    handleTasksClick,
    handleDrawerCancel
  }
}
